<!-- Account password dialog viewport fix V1.1; 2026-10-07 13:44:20 CST; parent d793d3cb3bff2269d2afd040be532ce7ae2044b3. -->
<!-- Account password setup V1.0; 2026-10-07 13:29:02 CST; parent 98f2abb400ae82aeb79450073a06825fca745614. -->
<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref } from 'vue';
import { consoleApi } from '../api/client';
import { useAuthStore } from '../stores/auth';
const props = defineProps<{ disabled?: boolean }>();
const emit = defineEmits<{ state: [value: 'SET' | 'UNSET' | 'UNKNOWN'] }>();
const auth = useAuthStore();
const state = ref<'SET' | 'UNSET' | 'UNKNOWN'>('UNKNOWN');
const email = ref('');
const loaded = ref(false);
const loading = ref(false);
const visible = ref(false);
const sending = ref(false);
const saving = ref(false);
const done = ref(false);
const error = ref('');
const notice = ref('');
const cooldown = ref(0);
const form = reactive({ verificationCode: '', newPassword: '', confirmation: '' });
let timer: ReturnType<typeof setInterval> | undefined;
async function load() {
  if (props.disabled || loading.value) return;
  loading.value = true;
  try {
    const result = await consoleApi.account.passwordState();
    if (!result.email || !['SET', 'UNSET', 'UNKNOWN'].includes(result.state)) throw new Error('账户信息不完整');
    email.value = result.email; state.value = result.state; loaded.value = true;
    emit('state', result.state); error.value = '';
  } catch { loaded.value = false; error.value = '暂时无法确认账户邮箱，请重新加载后再设置密码。'; }
  finally { loading.value = false; }
}
function open() {
  if (props.disabled || !loaded.value || done.value) return;
  form.verificationCode = ''; form.newPassword = ''; form.confirmation = '';
  error.value = ''; notice.value = ''; visible.value = true;
}
async function requireSameEmail() {
  const current = await consoleApi.account.passwordState();
  if (!current.email || current.email !== email.value) {
    loaded.value = false;
    throw new Error('账户邮箱已变化，请关闭弹窗并重新加载后再操作。');
  }
}
async function send() {
  if (props.disabled || !loaded.value || sending.value || saving.value || cooldown.value > 0) return;
  sending.value = true; error.value = '';
  try {
    await requireSameEmail();
    await consoleApi.auth.sendPasswordResetCode(email.value);
    notice.value = '验证码已发送至当前绑定邮箱，请查收。'; cooldown.value = 60;
    if (timer) clearInterval(timer);
    timer = setInterval(() => { if (--cooldown.value <= 0) { clearInterval(timer); timer = undefined; } }, 1000);
  } catch (e) { error.value = e instanceof Error ? e.message : '验证码发送失败'; }
  finally { sending.value = false; }
}
async function submit() {
  if (props.disabled || !loaded.value || saving.value || sending.value || done.value) return;
  error.value = '';
  if (!/^\d{6}$/.test(form.verificationCode.trim())) { error.value = '请输入6位邮箱验证码'; return; }
  if (form.newPassword.length < 8 || form.newPassword.length > 72 || !/[A-Za-z]/.test(form.newPassword) || !/\d/.test(form.newPassword)) {
    error.value = '密码须为8至72位，同时包含字母和数字'; return;
  }
  if (form.newPassword !== form.confirmation) { error.value = '两次密码输入不一致'; return; }
  saving.value = true;
  try {
    // Reuse the existing email verification, audit and session-revocation contract.
    await requireSameEmail();
    await consoleApi.auth.resetPassword({ email: email.value, verificationCode: form.verificationCode.trim(), newPassword: form.newPassword });
    form.verificationCode = ''; form.newPassword = ''; form.confirmation = '';
    done.value = true; auth.clearLocalSession();
    notice.value = '登录密码已设置，请重新登录。仍可使用 Google 登录。';
  } catch (e) { error.value = e instanceof Error ? e.message : '密码设置失败'; }
  finally { saving.value = false; }
}
onMounted(load);
onUnmounted(() => { if (timer) clearInterval(timer); });
defineExpose({ open, load });
</script>
<template>
  <div class="password-setup">
    <p v-if="disabled" class="status-help">代理访问期间不能修改客户密码或邮箱，请由客户本人操作。</p>
    <template v-else>
      <p class="status-help" v-if="state === 'UNSET'">尚未设置登录密码。请验证当前绑定邮箱后设置，无需填写 Google 邮箱密码。</p>
      <p class="status-help" v-else>未设置或忘记密码？可验证当前绑定邮箱后设置登录密码，Google 登录继续保留。</p>
      <el-button v-if="!loaded" :loading="loading" @click="load">重新加载</el-button>
      <el-button v-else :disabled="done" @click="open">{{ state === 'UNSET' ? '设置登录密码' : '邮箱验证设置密码' }}</el-button>
      <el-alert v-if="error && !visible" :title="error" type="error" :closable="false" />
    </template>
    <el-dialog v-model="visible" title="邮箱验证设置登录密码" class="account-password-dialog" append-to-body align-center width="min(460px, calc(100vw - 32px))" :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" @closed="form.newPassword = ''; form.confirmation = ''; form.verificationCode = ''">
      <p>当前绑定邮箱：<strong>{{ email }}</strong></p>
      <p class="status-help">设置成功后，原登录会话将失效，需要重新登录。</p>
      <el-alert v-if="error" :title="error" type="error" :closable="false" />
      <el-alert v-if="notice" :title="notice" type="success" :closable="false" />
      <template v-if="!done">
        <div class="inline-control">
          <el-input v-model="form.verificationCode" placeholder="邮箱验证码" maxlength="6" autocomplete="one-time-code" :disabled="saving" />
          <el-button :loading="sending" :disabled="saving || sending || cooldown > 0" @click="send">{{ cooldown > 0 ? `${cooldown}秒后重发` : '获取验证码' }}</el-button>
        </div>
        <el-input v-model="form.newPassword" type="password" show-password autocomplete="new-password" placeholder="新密码" :disabled="saving" />
        <el-input v-model="form.confirmation" type="password" show-password autocomplete="new-password" placeholder="确认新密码" :disabled="saving" />
      </template>
      <template #footer>
        <el-button v-if="done" tag="a" href="/login" type="primary">重新登录</el-button>
        <template v-else><el-button :disabled="saving" @click="visible = false">取消</el-button><el-button type="primary" :loading="saving" :disabled="saving || sending" @click="submit">确认设置</el-button></template>
      </template>
    </el-dialog>
  </div>
</template>
<style scoped>
.password-setup { margin: 10px 0; }
:global(.account-password-dialog .el-input), :global(.account-password-dialog .el-alert) { margin-bottom: 12px; }
</style>
